import React from "react";
import Select, {
  ActionMeta,
  Props as ReactSelectProps,
  SingleValue,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { Form } from "react-bootstrap";

export interface SelectOption {
  value: string | number;
  label: string;
  isDisabled?: boolean;
}

interface BaseProps {
  /** Optional label above the select */
  label?: string;
  /** Show red asterisk if required */
  isRequired?: boolean;
  /** Error message (from RHF or manual validation) */
  error?: string;
  /** Bootstrap group props */
  groupProps?: React.ComponentProps<typeof Form.Group>;
  labelProps?: React.ComponentProps<typeof Form.Label>;
}

type SelectInputProps = BaseProps &
  Omit<
    ReactSelectProps<SelectOption, false>,
    "onChange" | "value" | "defaultValue"
  > & {
    /** Controlled value (standalone mode) */
    value?: SelectOption | null;
    /** Default value (standalone uncontrolled) */
    defaultValue?: SelectOption | null;
    /** Callback when selection changes (standalone) */
    onChange?: (
      value: SelectOption | null,
      action: ActionMeta<SelectOption>
    ) => void;
    onCreateOption?: (newOption: SelectOption) => void;
  };

const SelectInput: React.FC<SelectInputProps> = ({
  label,
  isRequired = false,
  error,
  groupProps,
  labelProps = {},
  value,
  defaultValue,
  onChange,
  onCreateOption,
  ...selectProps
}) => {
  const handleChange = (
    option: SingleValue<SelectOption>,
    action: ActionMeta<SelectOption>
  ) => {
    onChange?.(option ?? null, action);
  };
  const { controlId, ...otherGroupProps } = groupProps;
  return (
    <>
      <Form.Group controlId={controlId} {...otherGroupProps}>
        {label && (
          <Form.Label {...labelProps}>
            {label} {isRequired && <span style={{ color: "red" }}>*</span>}
          </Form.Label>
        )}
        {onCreateOption ? (
          <CreatableSelect
            {...selectProps}
            name={controlId}
            classNamePrefix="select"
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            onCreateOption={(inputValue) => {
              const newOption = { label: inputValue, value: inputValue };

              onCreateOption?.(newOption);

              onChange?.(newOption, {
                action: "create-option",
                option: newOption,
              });
            }}
          />
        ) : (
          <Select
            {...selectProps}
            name={controlId}
            classNamePrefix="select"
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
          />
        )}
      </Form.Group>

      {error && (
        <span style={{ fontSize: 13 }} className="text-danger mt-1 d-block">
          {error}
        </span>
      )}
    </>
  );
};

export default SelectInput;
